import { Text } from '@chakra-ui/react';
import { useMemo } from 'react';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import type { MultiMetricDataMap } from '../../DataProvider/MultiMetricProvider';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import type { usePageConfig } from '../../PageConfigProvider/PageConfigProvider';
import { ChartFallback } from '../ChartFallback';
import type { useChart } from '../ChartProvider';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import { TextChart } from './TextChart';

export const TextChartCardContent = ({
  data,
  status,
  item,
  pageConfig,
  activeFilterMap,
}: {
  data?: { byMetric?: MultiMetricDataMap };
  status: DataProviderQueryStatus;
  item: NonNullable<ReturnType<typeof useChart>['item']>;
  pageConfig: ReturnType<typeof usePageConfig>['config'];
  activeFilterMap: ReturnType<typeof useFilterContext>['activeFilterMap'];
}) => {
  const multiData = useMemo(() => {
    const byMetric = data?.byMetric;
    if (!byMetric) return byMetric;
    const plotParameters = item?.plot?.plotParameters ?? [];
    const reportParameters =
      pageConfig.tabs?.[0]?.tabContent?.reportParameters ?? [];

    return processMultiMetricData({
      byMetric,
      plotParameters,
      reportParameters,
      activeFilterMap,
    });
  }, [data?.byMetric, item, pageConfig, activeFilterMap]);

  const isTextChart = item?.plot?.chartType === 'text';
  const isTextPromptDefined = !!item.plot?.textPrompt?.trim();
  const isTextHtmlNotDefined = !item.plot?.textHtml?.trim();
  const isWaitingForAi =
    isTextChart && isTextPromptDefined && isTextHtmlNotDefined;
  const effectiveStatus = isWaitingForAi
    ? DataProviderQueryStatus.Fetching
    : status;
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiTextPreview = gaChartPreviewMeta !== null && isTextChart;

  if (
    isGlobalAiTextPreview &&
    effectiveStatus === DataProviderQueryStatus.Error
  ) {
    return (
      <Text fontSize="12px" lineHeight="15px" color="#2D426A">
        Unable to load this summary.
      </Text>
    );
  }

  if (
    isGlobalAiTextPreview &&
    effectiveStatus === DataProviderQueryStatus.Fetching
  ) {
    return null;
  }

  return (
    <ChartFallback status={effectiveStatus}>
      <TextChart
        multiData={multiData}
        html={item.plot?.textHtml}
        isLoading={effectiveStatus === DataProviderQueryStatus.Fetching}
      />
    </ChartFallback>
  );
};

export const TextChartCardContentWithFilters = ({
  data,
  status,
  item,
  pageConfig,
}: {
  data?: { byMetric?: MultiMetricDataMap };
  status: DataProviderQueryStatus;
  item: NonNullable<ReturnType<typeof useChart>['item']>;
  pageConfig: ReturnType<typeof usePageConfig>['config'];
}) => {
  const { activeFilterMap } = useFilterContext();

  return (
    <TextChartCardContent
      data={data}
      status={status}
      item={item}
      pageConfig={pageConfig}
      activeFilterMap={activeFilterMap}
    />
  );
};
